/**
 * WS chat manager — a module-level singleton that owns the ONE multiplexed
 * WebSocket to chatbot-api plus per-conversation state, decoupled from React
 * component lifecycles.
 *
 * Responsibilities:
 *  - One socket per user; frames carry a `sessionId` routing field both ways,
 *    so any number of conversations share the connection (no reconnect on
 *    conversation switch, no extra bootstrap).
 *  - Per-conversation reducer state lives here, so a conversation keeps
 *    streaming in the background while the UI shows another one (or none).
 *  - Connection-level auto-reconnect with backoff + presence gating. After a
 *    reconnect the backend re-attaches live conversations and keeps streaming;
 *    if a stuck in-flight turn gets no frames shortly after reconnecting, the
 *    conversation is resynced from persisted history instead.
 *
 * React consumes this through useSyncExternalStore-compatible
 * subscribe/getState pairs (see useWsChat).
 */
import { ConnectionStatus } from '@yepai/chatbot-sdk-core';
import {
  bootstrapWsChat,
  buildChatWsUrl,
  clearWsChatSession,
  createWsChatTraceId,
  getConversationDetail,
} from '../wsChatApi';
import { openWsChatConnection, type WsChatConnection } from './connection';
import type { WsChatEvent } from './protocol';
import {
  initialWsChatState,
  wsChatReducer,
  type WsChatAction,
  type WsChatRunState,
} from './runtime';
import { CLIENT_ERROR_CODES } from './turnErrorNotice';
import {
  EMPTY_PUBLISH_OPS,
  mergePublishOps,
  mergePublishOpsSnapshot,
  type PublishOpsState,
} from './publishOps';
import { listPublishOperations } from '../publishOpsApi';

// Backoff schedule for auto-reconnect (ms). Length also bounds max attempts.
const RECONNECT_DELAYS_MS = [1000, 2000, 4000, 8000, 16000, 30000];
// "Recently active" window: only auto-reconnect if the user sent something
// within this window (or a reply is still in-flight).
const RECENT_ACTIVITY_WINDOW_MS = 60_000;
// Keepalive + liveness-check interval (LBs tend to kill idle WebSockets after
// ~60s). Each tick sends a ping AND checks for a half-open socket.
const PING_INTERVAL_MS = 15_000;
// While a turn is streaming, frames arrive continuously (chunks + thinking). If
// the receive side goes silent this long, the socket is half-open (send still
// works, `isOpen()` still true, no close ever fires) — force a reconnect so the
// existing recover/resync path can heal the stuck turn. Kept well above any
// realistic silent-thinking gap to avoid false positives.
const STALL_TIMEOUT_MS = 45_000;
// After a reconnect, give the re-attached backend this long to resume
// streaming before falling back to a history resync for stuck turns.
const POST_RECONNECT_RESYNC_DELAY_MS = 3_000;
// How long after a turn started we still try to resume it on a fresh page load.
// Matches the backend's detached-reply-max (`digital-staff.ws.detached-reply-max-secs`,
// 300s): past this the upstream session is gone, so a refresh just shows history.
const RESUME_INFLIGHT_WINDOW_MS = 300_000;

/**
 * [3.5 #16] Stuck-turn watchdog. A conversation that still reads as "being
 * worked on" but has received no frame for this long is treated as dead and
 * reconciled against persisted history, so the spinner can never run forever
 * and the composer can never stay locked (it is disabled while phase is
 * thinking/streaming — see useWsChat's `isBusy`).
 */
const STUCK_TURN_TIMEOUT_MS = 90_000;
/**
 * A message sent while a turn was running is held by chatbot-api and goes up as a turn of its
 * own once that turn ends, announced with `{type:'resume', replyInFlight:true}` (chatbot-api
 * #2412). Between the running turn's `done` and that next turn's first frame the conversation
 * stays "being worked on" for at most this long; if nothing arrives by then it goes back to idle
 * quietly — no error, the message is in the conversation either way.
 */
const QUEUED_TURN_START_TIMEOUT_MS = 30_000;
// The same watchdog on a socket that is still OPEN. Here silence is not
// evidence of death: while the model writes a long tool call (a 1,200-word
// article is one CREATE_CONTENT_DRAFT argument) nothing streams for minutes,
// and chatbot-api's own reply-stall check only gives up after 600s
// (`digital-staff.ws.reply-stall-timeout-secs`, raised from 240 on 2026-09-18
// after healthy composes of 315-325s) — it then sends an error frame that
// unlocks the UI. Measured on dev 2026-09-08: a 3.5-minute compose tripped
// the 90s check, the resync turned the half-turn into a persisted bubble
// carrying the model's pre-tool narration, and the real reply landed as a
// second bubble. So on a live socket wait past the server's own verdict; the
// 90s figure stays for a socket that is actually gone.
//
// 660s = the server's 600s plus a minute (the same figure as its
// detached-reply-max-secs). This was 300s — below the server's 600s — and on
// test 2026-10-23 it unlocked the composer in the middle of a calendar batch
// segment: the merchant's "use a casual tone" reached the agent behind the
// running segment, and when the segment later died the batch was stranded at
// 3 of 5 with "Maya had trouble connecting" (D3). Keep this ABOVE the server's
// stall timeout whenever that one moves.
const STUCK_TURN_LIVE_SOCKET_TIMEOUT_MS = 660_000;

/** How often the stuck-turn watchdog looks for dead turns. */
const WATCHDOG_INTERVAL_MS = 30_000;
// localStorage key (per user) holding in-flight turn markers so a refresh knows
// which conversations to reconnect and keep waiting on: { [sessionId]: at }.
const INFLIGHT_KEY_PREFIX = 'wschat:inflight';

/**
 * Connection-level, agent-agnostic frame types that chatbot-api's WS proxy
 * sends directly on the socket with no sessionId (see `route`'s doc comment):
 * credit exhaustion and generic connection errors (which also covers the
 * "too many conversations" session-limit rejection — both are backend
 * `{type:'error', ...}` frames, parsed by `frameToEvent`). Everything else is
 * conversation-scoped and requires a sessionId to be routed safely.
 */
function isGlobalFrame(event: WsChatEvent): boolean {
  return event.type === 'error' || event.type === 'credit-exhausted';
}

/**
 * A connect attempt found itself superseded (teardown / user switch) when its
 * handshake completed. Sentinel so ensureConnection can tell this apart from a
 * real connect failure: it must reject the caller (a silent resolve would read
 * as "reconnected" to e.g. resumeInflightSessions) but never trigger the
 * fresh-token retry, which would install a connection the teardown just forbade.
 */
class StaleConnectError extends Error {
  constructor() {
    super('WS chat connection was torn down while connecting');
    this.name = 'StaleConnectError';
  }
}

/** The user is "present" when the tab is visible and the browser is online. */
function userPresent(): boolean {
  if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return false;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  return true;
}

export interface WsChatSessionEntry {
  state: WsChatRunState;
  /** A reply is in-flight for this conversation. */
  pendingReply: boolean;
  /** Persisted history has been loaded into state (vs. live frames only). */
  hydrated: boolean;
  /** ms timestamp of the last server frame routed to this conversation. */
  lastEventAt: number;
  /**
   * Server-side publish operations of this conversation, by op id (`publish_op` frames +
   * the REST backfill — see publishOps.ts). Kept OFF the turn reducer's state on purpose:
   * `hydrate` replaces that state wholesale, and a history resync must not wipe what the
   * publish executor has reported. Absent = nothing known (no switch / no publish yet).
   */
  publishOps?: PublishOpsState;
  /**
   * The conversation's earlier messages are in `state` — loaded by `hydrate` / `mergeHistory`,
   * or there were none because this tab started the conversation (first send on an empty
   * slot). Until then a publish op that no card claims may only be waiting for its card to
   * arrive with the history, so it is not drawn as a standalone block yet.
   */
  historyReady?: boolean;
}

interface SessionSlot {
  entry: WsChatSessionEntry;
  listeners: Set<() => void>;
  /** Which agent this conversation belongs to (marketing, operation, …). */
  agentType: string;
  /** The text of the last message sent on this conversation — what the one-copy-per-turn
   *  guard in `sendMessage` compares against. Kept off `entry` so it never re-renders. */
  lastSentText: string | null;
  /** ms timestamp of the last send on this conversation (0 = never). */
  lastSentAt: number;
  /** Messages sent while this conversation's turn was running, not answered yet — each one
   *  is queued by chatbot-api and becomes the next turn. See QUEUED_TURN_START_TIMEOUT_MS. */
  queuedUnanswered: number;
  /** Running while a `done` has arrived but the queued turn after it has not started yet. */
  queuedHoldTimer: ReturnType<typeof setTimeout> | null;
  /** A view showing publish progress has opened this conversation — refetch its publish ops
   *  after every (re)connect, since `publish_op` frames are not buffered while we were away. */
  publishOpsWatched: boolean;
}

type Listener = () => void;

class WsChatManager {
  private userId: string | undefined;
  /**
   * The conversation the UI was last viewing, per agentType — restored across
   * navigation. Scoped per agentType so mounting one agent's chat never
   * restores a different agent's active conversation (e.g. Oscar mounting
   * after Maya must not inherit Maya's session).
   */
  private activeSessionIdByAgent = new Map<string, string | null>();
  private conn: WsChatConnection | null = null;
  private connecting: Promise<void> | null = null;
  /** Whether the in-flight `connecting` attempt was FORCED (no fresh-token
   *  retry on failure) — see the join fallback in ensureConnection. */
  private connectingIsForced = false;
  /**
   * Identity of the connect attempt whose socket may act on the manager.
   * Bumped when a new connect starts and when the connection is torn down, so
   * a superseded or already-closed socket's late events read as stale even
   * while `this.conn` is null (e.g. right after an idle close). Checking
   * `this.conn` identity alone had exactly that hole — see ensureConnection.
   */
  private connEpoch = 0;
  private status: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private statusListeners = new Set<Listener>();
  private sessions = new Map<string, SessionSlot>();
  private turnCompleteListeners = new Set<(sessionId: string) => void>();

  private lastActivityAt = 0;
  private intentionalClose = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private awaitingPresence = false;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  /** [3.5 #16] Stuck-turn watchdog; runs while anything is mounted. */
  private watchdogTimer: ReturnType<typeof setInterval> | null = null;
  /** Mounted consumers; the socket closes when none remain and nothing streams. */
  private refCount = 0;
  private presenceBound = false;
  /**
   * The agentType that opened (or last (re)opened) the shared connection —
   * set by `ensureConnection(agentType)`. The connect-retry (fresh-token)
   * path and the auto-reconnect path both reuse this stored value rather
   * than re-deriving or defaulting to any particular agent.
   */
  private connectionAgentType = 'marketing';
  /**
   * agentTypes that have been bootstrapped (primed) on the CURRENT connection.
   * Cleared whenever the connection is torn down (reset, intentional close,
   * or the socket's own onClose) so a fresh connection re-primes every agent
   * it's asked to serve. See `ensureAgentReady`.
   */
  private readyAgents = new Set<string>();

  // ---------- consumer (hook) API ----------

  /** Reset everything when the signed-in user changes. */
  setUser(userId: string | undefined): void {
    if (this.userId === userId) return;
    this.reset();
    this.userId = userId;
  }

  /** Full reset — used on sign-out/user change and by tests. */
  reset(): void {
    this.userId = undefined;
    this.activeSessionIdByAgent.clear();
    clearWsChatSession();
    this.stopWatchdog();
    this.closeConnection();
    this.sessions.forEach(slot => this.clearQueuedHold(slot));
    this.sessions.forEach(slot => slot.listeners.forEach(l => l()));
    this.sessions.clear();
    this.lastActivityAt = 0;
    this.reconnectAttempts = 0;
    this.intentionalClose = false;
    this.readyAgents.clear();
  }

  retain(): void {
    this.refCount += 1;
    this.bindPresence();
    this.startWatchdog();
  }

  release(): void {
    this.refCount = Math.max(0, this.refCount - 1);
    if (this.refCount === 0) this.stopWatchdog();
    this.maybeIdleClose();
  }

  getStatus(): ConnectionStatus {
    return this.status;
  }

  /**
   * The conversation the UI last viewed for a given agentType, so a remount
   * can restore it — never a different agentType's active conversation.
   */
  getActiveSessionId(agentType: string): string | null {
    const mem = this.activeSessionIdByAgent.get(agentType);
    if (mem !== undefined) return mem;
    // Restore across a full page reload — the manager singleton is memory-only,
    // so without this a refresh loses the active conversation and drops the user
    // onto a fresh "New chat" instead of the conversation they were viewing.
    try {
      return localStorage.getItem(this.activeSessionKey(agentType)) || null;
    } catch {
      return null;
    }
  }

  setActiveSessionId(sessionId: string | null, agentType: string): void {
    this.activeSessionIdByAgent.set(agentType, sessionId);
    try {
      if (sessionId) {
        localStorage.setItem(this.activeSessionKey(agentType), sessionId);
        localStorage.removeItem(this.newChatKey(agentType));
      } else {
        localStorage.removeItem(this.activeSessionKey(agentType));
        // Null here is the merchant asking for a new chat — remember THAT, so a reload can tell
        // "they were on a blank new chat" from "nothing was ever recorded" (see below).
        localStorage.setItem(this.newChatKey(agentType), '1');
      }
    } catch {
      /* localStorage unavailable — fall back to memory-only restore */
    }
  }

  /**
   * Whether anything is recorded about what the merchant last viewed for this agentType: a
   * conversation, or a blank new chat they opened. False only when there is no record at all
   * (first visit on this browser, storage cleared) — the one case where a reload should fall back
   * to the most recently active conversation instead of guessing "new chat".
   */
  hasActiveSessionRecord(agentType: string): boolean {
    if (this.activeSessionIdByAgent.has(agentType)) return true;
    try {
      return (
        !!localStorage.getItem(this.activeSessionKey(agentType)) ||
        !!localStorage.getItem(this.newChatKey(agentType))
      );
    } catch {
      return false;
    }
  }

  /** Per-(user, agentType) key so a reload restores the right conversation and
   *  users never see each other's. */
  private activeSessionKey(agentType: string): string {
    return `wschat:active:${this.userId ?? 'anon'}:${agentType}`;
  }

  /** Set while the last thing viewed is a blank new chat (no conversation id yet). */
  private newChatKey(agentType: string): string {
    return `wschat:active-new:${this.userId ?? 'anon'}:${agentType}`;
  }

  // ---------- in-flight turn markers (survive a page refresh) ----------
  // A turn runs upstream even while the browser is gone (the proxy keeps the
  // ZeroClaw session alive and re-attaches on reconnect). But a full refresh
  // wipes all in-memory state, so nothing reconnects and the half-finished turn
  // looks stopped. We persist a marker per in-flight turn; on the next mount
  // `resumeInflightSessions()` reconnects and keeps waiting.

  private inflightKey(): string {
    return `${INFLIGHT_KEY_PREFIX}:${this.userId ?? 'anon'}`;
  }

  private readInflight(): Record<string, { agentType: string; at: number }> {
    try {
      const raw = localStorage.getItem(this.inflightKey());
      const obj = raw ? JSON.parse(raw) : null;
      return obj && typeof obj === 'object' ? obj : {};
    } catch {
      return {};
    }
  }

  private writeInflight(map: Record<string, { agentType: string; at: number }>): void {
    try {
      if (Object.keys(map).length === 0) localStorage.removeItem(this.inflightKey());
      else localStorage.setItem(this.inflightKey(), JSON.stringify(map));
    } catch {
      /* localStorage unavailable — resume-on-refresh just won't fire */
    }
  }

  private markInflight(sessionId: string, agentType: string): void {
    const map = this.readInflight();
    map[sessionId] = { agentType, at: Date.now() };
    this.writeInflight(map);
  }

  private clearInflight(sessionId: string): void {
    const map = this.readInflight();
    if (map[sessionId]) {
      delete map[sessionId];
      this.writeInflight(map);
    }
  }

  /**
   * On a fresh mount (e.g. after a page refresh), reconnect any conversation
   * whose turn was still in-flight recently and keep showing it as generating.
   * Opening the socket makes the proxy auto-re-attach the still-running session
   * (and send a `resume` frame); `recoverStuckTurns()` is the safety net if the
   * upstream turn already ended while we were gone (falls back to history).
   */
  resumeInflightSessions(): void {
    const now = Date.now();
    const map = this.readInflight();
    // D3 (test 2026-10-24): every chat view calls this on mount, not only the
    // first one after a refresh. When the socket is still OPEN nothing has been
    // reconnected: the proxy already attached every live conversation to this
    // socket when it connected, and frames for them keep arriving. Running
    // `recoverStuckTurns()` here anyway read a turn that was merely silent (the
    // model reading products / composing an article — minutes without a frame)
    // as "finalized while we were away": the merchant sent "write 5 drafts" to
    // Maya, clicked Oscar → Anna, and 3s later Maya's turn was resynced to the
    // request bubble and marked idle; the next route change then idle-closed the
    // socket and all 5 drafts + the final reply went to no one until a refresh.
    // An open socket is the live source of truth — leave its turns alone (the
    // stuck-turn watchdog still covers a turn that truly died). A connect that is
    // already in flight counts too: it has its own initiator (a send, which then
    // streams, or a reconnect / earlier resume that runs its own recovery), and
    // the proxy re-attaches every live conversation — with a `resume` frame —
    // the moment it lands.
    const alreadyConnected = !!this.conn?.isOpen() || !!this.connecting;
    let agentType: string | undefined;
    let pruned = false;
    for (const [sessionId, mark] of Object.entries(map)) {
      if (!mark || now - mark.at > RESUME_INFLIGHT_WINDOW_MS) {
        delete map[sessionId];
        pruned = true;
        continue;
      }
      if (alreadyConnected) continue;
      const slot = this.slot(sessionId, mark.agentType);
      this.setEntry(slot, { pendingReply: true });
      agentType = agentType ?? mark.agentType;
    }
    if (pruned) this.writeInflight(map);
    if (!agentType) return;
    this.lastActivityAt = now;
    this.ensureConnection(agentType)
      .then(() => this.recoverStuckTurns())
      .catch(e => console.warn('[WS chat] resume-on-mount connect failed:', e));
  }

  subscribeStatus(listener: Listener): () => void {
    this.statusListeners.add(listener);
    return () => this.statusListeners.delete(listener);
  }

  getSession(sessionId: string): WsChatSessionEntry | undefined {
    return this.sessions.get(sessionId)?.entry;
  }

  /** Which agentType a conversation belongs to (set when its slot was first created via sendMessage). */
  getSessionAgentType(sessionId: string): string | undefined {
    return this.sessions.get(sessionId)?.agentType;
  }

  subscribeSession(sessionId: string, listener: Listener): () => void {
    const slot = this.slot(sessionId);
    slot.listeners.add(listener);
    return () => slot.listeners.delete(listener);
  }

  /** Fires whenever any conversation's turn completes (sidebar refresh). */
  subscribeTurnComplete(listener: (sessionId: string) => void): () => void {
    this.turnCompleteListeners.add(listener);
    return () => this.turnCompleteListeners.delete(listener);
  }

  isBusy(sessionId: string): boolean {
    const phase = this.sessions.get(sessionId)?.entry.state.phase;
    return phase === 'thinking' || phase === 'streaming';
  }

  /** Apply a local action (hydrate, reset, approval-decided, …). */
  dispatch(sessionId: string, action: WsChatAction): void {
    const slot = this.slot(sessionId);
    this.setEntry(slot, { state: wsChatReducer(slot.entry.state, action) });
  }

  /**
   * Load persisted history into a conversation's state.
   *
   * `agentType`, when supplied, stamps the slot so a history-only conversation
   * (opened via `onSelectConversation` and never sent to) still ends up with a
   * real agentType on its slot — otherwise a later reconnect resync would have
   * nothing but the `''` placeholder `slot()` assigns to a slot created via
   * `dispatch`/`route`/`subscribeSession` before any `sendMessage`. Only
   * stamps when the slot doesn't already have one, so an established slot's
   * agentType (set at creation via `sendMessage`) is never overwritten.
   */
  hydrate(
    sessionId: string,
    messages: WsChatRunState['messages'],
    traces: WsChatRunState['traces'],
    agentType?: string
  ): void {
    const slot = this.slot(sessionId, agentType);
    if (agentType && !slot.agentType) slot.agentType = agentType;
    const hydrated = wsChatReducer(slot.entry.state, {
      type: 'hydrate',
      messages,
      sessionId,
      traces,
    });
    this.setEntry(slot, {
      // History has no notion of the queued turn still to come (holdForQueuedTurn) — a
      // post-turn resync landing in that window must not drop the conversation back to idle.
      state: slot.queuedHoldTimer
        ? wsChatReducer(hydrated, { type: 'resume', replyInFlight: true })
        : hydrated,
      hydrated: true,
      historyReady: true,
    });
  }

  /**
   * Merge persisted history UNDER a live in-flight turn without clobbering it —
   * so opening a recent chat whose turn was resumed mid-flight shows the earlier
   * history immediately, not just the live "thinking" state (YEP-709). The slot
   * stays NOT `hydrated` (the live turn is still the source of truth); the
   * post-turn backfill/resync reconciles the final state.
   */
  mergeHistory(
    sessionId: string,
    messages: WsChatRunState['messages'],
    traces: WsChatRunState['traces'],
    agentType?: string
  ): void {
    const slot = this.slot(sessionId, agentType);
    if (agentType && !slot.agentType) slot.agentType = agentType;
    this.setEntry(slot, {
      state: wsChatReducer(slot.entry.state, {
        type: 'hydrate-merge',
        messages,
        sessionId,
        traces,
      }),
      historyReady: true,
    });
  }

  /**
   * Send a user message on a conversation. Opens/reuses the shared socket;
   * never tears down other conversations.
   */
  async sendMessage(
    sessionId: string,
    text: string,
    agentType: string,
    options?: { silent?: boolean; scanKeywords?: string[] }
  ): Promise<void> {
    const slot = this.slot(sessionId, agentType);
    // One copy of a message per turn (10-26, kiechee): "Looks good — publish it to my Shopify
    // blog." reached the agent twice, 7s apart, while the first was still being answered, and
    // the second copy is what chatbot-api then waited on until its stall watchdog fired. Since
    // chatbot-api #2412 a message sent mid-turn is queued and answered as a turn of its own —
    // so a repeat would be done twice (published twice). Every way of sending lands here
    // (composer, card buttons, Retry, hand-offs, the publish executor), so a repeat is refused
    // here: same conversation, same text, while that conversation is still working on it. A
    // different message still goes through, and so does the same one once the turn is over.
    if (this.isBusy(sessionId) && slot.lastSentText === text) {
      console.warn('[WS chat] not sending a repeat of the message this turn is already answering', {
        sessionId,
      });
      return;
    }
    slot.lastSentText = text;
    slot.lastSentAt = Date.now();
    // First send on an empty, never-loaded slot = a conversation this tab is starting: there is
    // no earlier history to wait for. (Sending into an existing conversation before its history
    // arrives happens only while the view shows its history spinner; mergeHistory follows.)
    if (!slot.entry.historyReady && slot.entry.state.messages.length === 0) {
      this.setEntry(slot, { historyReady: true });
    }
    // Sent while a turn is running: chatbot-api holds it and answers it as the next turn.
    const midTurn = this.isBusy(sessionId);
    // `silent` sends the text to the agent WITHOUT adding a user bubble — used for
    // the hidden `[session:opened]` onboarding trigger. pendingReply still flips so
    // the UI shows Maya "working" until the opener streams in.
    this.setEntry(slot, {
      state: options?.silent
        ? slot.entry.state
        : wsChatReducer(slot.entry.state, { type: 'user-message', text }),
      pendingReply: true,
    });
    // Persist an in-flight marker so a refresh mid-turn can reconnect + resume.
    this.markInflight(sessionId, agentType);
    this.lastActivityAt = Date.now();
    this.reconnectAttempts = 0;
    this.clearReconnectTimer();

    try {
      // Lazy per-(userId, agentType) readiness: the first action against an
      // agent bootstraps it (and opens the shared socket if needed) here,
      // rather than on mount.
      await this.ensureAgentReady(agentType);
      // Open-then-close race: the socket was open when ensureAgentReady resolved
      // but the backend closed it before we could send here — an LB idle-drop,
      // or upstream provisioning closing a freshly handshaked socket (onClose
      // has since nulled this.conn). Rather than surfacing a dead-end raw
      // "WebSocket is not open", force ONE fresh reconnect and retry — this is
      // the "lazy reconnect on next send" the unexpected-drop path already
      // promises, which previously didn't cover this in-send window.
      if (!this.conn?.isOpen()) {
        await this.ensureConnection(agentType, true);
      }
      if (!this.conn?.isOpen()) throw new Error('Chat connection lost. Please try again.');
      this.conn.send(text, sessionId, agentType, options?.scanKeywords);
      if (midTurn) slot.queuedUnanswered += 1;
    } catch (e) {
      console.error('[WS chat] sendMessage failed:', e);
      this.setEntry(slot, {
        state: wsChatReducer(slot.entry.state, {
          type: 'error',
          message: e instanceof Error ? e.message : 'Failed to send',
          // It never left this browser — turnErrorNotice says so instead of "it stopped".
          code: CLIENT_ERROR_CODES.SEND_FAILED,
        }),
        pendingReply: false,
      });
      this.clearInflight(sessionId);
      throw e;
    }
  }

  respondToApproval(
    sessionId: string,
    requestId: string,
    decision: 'approve' | 'always' | 'deny'
  ): void {
    this.conn?.sendRaw({
      type: 'approval_response',
      request_id: requestId,
      decision,
      sessionId,
    });
    this.dispatch(sessionId, { type: 'approval-decided', id: requestId, decision });
  }

  // ---------- server-side publish operations ----------

  /** The conversation's publish ops (stable empty object when none are known). */
  getPublishOps(sessionId: string): PublishOpsState {
    return this.sessions.get(sessionId)?.entry.publishOps ?? EMPTY_PUBLISH_OPS;
  }

  /**
   * Merge publish ops (frame, REST backfill, or an abandon/confirm response) into a
   * conversation. Order-safe: an op older than what is already held is ignored
   * (publishOps.isNewerPublishOp). No-op — and no re-render — when nothing changed.
   */
  applyPublishOps(sessionId: string, ops: unknown[]): void {
    const slot = this.slot(sessionId);
    const prev = slot.entry.publishOps ?? EMPTY_PUBLISH_OPS;
    const next = mergePublishOps(prev, ops);
    if (next === prev) return;
    this.setEntry(slot, { publishOps: next });
  }

  /**
   * Merge a REST snapshot (§5.1) and drop held ops the server no longer returns (logically
   * deleted) — but only when the snapshot is the conversation's whole list (fewer than the
   * 100-row cap) and only ops held before the read (see publishOps.mergePublishOpsSnapshot).
   */
  private applyPublishOpsSnapshot(
    sessionId: string,
    ops: unknown[],
    knownBefore: ReadonlySet<string>
  ): void {
    const slot = this.slot(sessionId);
    const prev = slot.entry.publishOps ?? EMPTY_PUBLISH_OPS;
    const next = mergePublishOpsSnapshot(prev, ops, knownBefore);
    if (next === prev) return;
    this.setEntry(slot, { publishOps: next });
  }

  /**
   * A view that shows publish progress opened this conversation: backfill its ops now, and
   * again after every (re)connect from here on. Frames are not buffered server-side, so
   * anything that changed while the socket was down only comes back through this read.
   * Failures are swallowed — an older backend without the endpoint just shows nothing.
   */
  watchPublishOps(sessionId: string): void {
    const slot = this.slot(sessionId);
    slot.publishOpsWatched = true;
    void this.refreshPublishOps(sessionId);
  }

  async refreshPublishOps(sessionId: string): Promise<void> {
    const userId = this.userId;
    if (!userId || !sessionId) return;
    // Ops held before the read goes out. Only these may be dropped for being absent from the
    // snapshot — one that arrives by frame while the read is in flight is simply newer than it.
    const knownBefore = new Set(Object.keys(this.getPublishOps(sessionId)));
    try {
      const ops = await listPublishOperations(userId, sessionId);
      // Signed out / switched user while the read was in flight.
      if (this.userId !== userId) return;
      this.applyPublishOpsSnapshot(sessionId, ops, knownBefore);
    } catch (e) {
      console.debug('[WS chat] publish-op backfill failed (ignored):', e);
    }
  }

  /** After a (re)connect: backfill every watched conversation someone is looking at. */
  private refreshWatchedPublishOps(): void {
    this.sessions.forEach((slot, sessionId) => {
      if (slot.publishOpsWatched && slot.listeners.size > 0) void this.refreshPublishOps(sessionId);
    });
  }

  // ---------- internals ----------

  /**
   * `agentType` is only known at slot-creation time when called from
   * `sendMessage`; other call sites (dispatch/hydrate/route/subscribeSession)
   * look up an existing slot and don't need to supply it. If a slot doesn't
   * exist yet for one of those, we don't have an agentType to attribute it
   * to — leave it unset (empty string) rather than guessing.
   */
  private slot(sessionId: string, agentType?: string): SessionSlot {
    let slot = this.sessions.get(sessionId);
    if (!slot) {
      slot = {
        entry: {
          state: { ...initialWsChatState(), sessionId },
          pendingReply: false,
          hydrated: false,
          lastEventAt: 0,
        },
        listeners: new Set(),
        agentType: agentType ?? '',
        lastSentText: null,
        lastSentAt: 0,
        queuedUnanswered: 0,
        queuedHoldTimer: null,
        publishOpsWatched: false,
      };
      this.sessions.set(sessionId, slot);
    }
    return slot;
  }

  private setEntry(slot: SessionSlot, patch: Partial<WsChatSessionEntry>): void {
    slot.entry = { ...slot.entry, ...patch };
    slot.listeners.forEach(l => l());
  }

  private setStatus(status: ConnectionStatus): void {
    if (this.status === status) return;
    this.status = status;
    this.statusListeners.forEach(l => l());
  }

  /**
   * Ready a specific agent independent of socket state and independent of
   * WHICH agentType actually drove the connection open. Cold-connects the
   * socket if needed (delegating to `ensureConnection`, which bootstraps
   * internally while opening — bootstrapping here too would double-call
   * `bootstrapWsChat`). Then, if the now-open connection hasn't bootstrapped
   * THIS agentType yet, primes it directly.
   *
   * The second step is what makes this safe under connection-reuse races:
   * `ensureConnection` collapses concurrent callers onto a single in-flight
   * connect promise (see its `this.connecting` short-circuit), so if agent A
   * is mid-connect when agent B calls in, B's `ensureConnection(B)` just
   * awaits A's in-flight connect — the socket opens bootstrapped for A only.
   * `readyAgents` (populated by `ensureConnection`'s `open()` for whichever
   * agentType it actually bootstrapped) lets us detect that gap here and
   * prime B on the now-open socket, regardless of which agent's connect
   * attempt was reused.
   *
   * Priming on an already-open socket is best-effort: a transient 5xx or a
   * just-expired token cache must never fail a send on an otherwise healthy
   * connection. Correctness holds even for an agentType that was NEVER primed
   * on this connection — the WS proxy provisions per session on frame arrival
   * (DigitalStaffWsProxyHandler.sessionFor → provisioningService.wsSession →
   * ensureAgent + ensureRunning), so priming only pre-warms that path. The
   * failed agentType is deliberately NOT added to `readyAgents`, so the next
   * ensureAgentReady retries the prime. When the socket is NOT open, the
   * failure must still propagate through `ensureConnection` — there's no
   * connection to fail-open on.
   */
  async ensureAgentReady(agentType: string): Promise<void> {
    const userId = this.userId;
    if (!userId) throw new Error('No user available for WS chat');
    if (!this.conn?.isOpen()) {
      await this.ensureConnection(agentType); // opens socket; open() adds its agentType to readyAgents
    }
    // Prime THIS agentType if the open connection hasn't bootstrapped it yet
    // (covers: socket already open for another agent; and the connecting-reuse
    // race where ensureConnection returned an in-flight connect for a different agent).
    if (this.conn?.isOpen() && !this.readyAgents.has(agentType)) {
      try {
        await bootstrapWsChat(userId, agentType, false);
        this.readyAgents.add(agentType);
      } catch (e) {
        console.warn(
          `[WS chat] agent priming failed on open socket (continuing; agentType=${agentType}, will retry on next send):`,
          e
        );
      }
    }
  }

  /**
   * Single in-flight connect attempt; resolves once the socket is open.
   * `agentType`, when provided, becomes the new stored default
   * (`connectionAgentType`) used to bootstrap this connection and any
   * later retry/reconnect that doesn't have its own agentType to supply.
   */
  ensureConnection(agentType?: string, force = false): Promise<void> {
    if (agentType) this.connectionAgentType = agentType;
    if (!force && this.conn?.isOpen()) return Promise.resolve();
    // One connect in flight, ever — forced callers included. A forced connect
    // that bypassed this dedup could run beside a normal one; both finished
    // with `this.conn = conn`, and the loser's socket stayed OPEN with its
    // handlers attached, routing every frame into the manager a second time
    // (YEP-787: each reply rendered twice). Joining the in-flight attempt is
    // sound for force too: it always yields a brand-new socket.
    if (this.connecting) {
      // A non-forced caller joining a FORCED attempt must not inherit its
      // no-retry policy: a forced attempt throws on its first failure, while
      // a user-initiated send is entitled to the fresh-token retry. Fall back
      // to a fresh attempt of its own — except after a teardown
      // (StaleConnectError), which forbade reconnecting on purpose.
      if (!force && this.connectingIsForced) {
        return this.connecting.catch(e => {
          if (e instanceof StaleConnectError) throw e;
          return this.ensureConnection(agentType);
        });
      }
      return this.connecting;
    }

    const run = (async () => {
      const userId = this.userId;
      if (!userId) throw new Error('No user available for WS chat');
      this.setStatus(ConnectionStatus.CONNECTING);
      // We're (re)connecting on purpose; a later unexpected drop should reconnect.
      this.intentionalClose = false;
      // Close the socket being replaced UP FRONT, not after the new one opens.
      // The epoch bump below makes its handlers stale immediately, so if it
      // were kept installed and this connect then failed, the manager would
      // hold an open socket that can still send but whose every reply is
      // discarded — a deaf connection nothing would ever tear down (codex
      // review P2). close() detaches its handlers, so it can neither route
      // frames nor fire a late onClose; the next send lazily reconnects.
      const superseded = this.conn;
      this.conn = null;
      if (superseded) {
        superseded.close();
        // close() detaches the socket's onClose, so ITS readyAgents.clear()
        // never runs — clear here: priming is per-connection and dies with
        // the socket being replaced.
        this.readyAgents.clear();
      }

      const open = async (forceToken: boolean) => {
        // Bumped synchronously at attempt start: from this instant every
        // earlier socket (however it ends up torn down) is stale to its
        // handlers. Per ATTEMPT, not per ensureConnection call — the
        // fresh-token retry below goes through closeConnection(), whose own
        // bump would otherwise mark the retry's socket stale to itself.
        const epoch = ++this.connEpoch;
        const isStale = () => this.connEpoch !== epoch;
        try {
          // Capture before the await: a racing ensureConnection(otherAgentType)
          // call (reusing this in-flight connect) mutates `connectionAgentType`
          // out from under us — the bootstrap call and the readyAgents.add
          // below must both refer to the agentType THIS open() actually primed.
          const bootstrapAgentType = this.connectionAgentType;
          const session = await bootstrapWsChat(userId, bootstrapAgentType, forceToken);
          if (isStale()) {
            // Torn down while the bootstrap was pending: stop before opening
            // a socket with the (old) captured userId's token, and before
            // re-adding the old agentType to the readyAgents the teardown
            // just cleared.
            throw new StaleConnectError();
          }
          this.readyAgents.add(bootstrapAgentType);
          const traceId = createWsChatTraceId();
          // No per-conversation URL params — routing is per-frame (multiplexed).
          const url = buildChatWsUrl(session, null, traceId);

          // Every handler below checks it still speaks for the CURRENT
          // connect epoch. A superseded socket can linger open (its close is
          // async) and its late events must not act on the manager:
          // chatbot-api fans every frame out to every attached browser
          // connection, so a stale socket's onEvent re-delivers each frame —
          // `done` has no reducer dedup, and each copy rendered another
          // identical reply bubble (YEP-787). A stale onClose was just as
          // bad: it nulled `this.conn` (clobbering the live socket's
          // reference) and scheduled a reconnect, opening a THIRD socket and
          // making the duplication self-sustaining. The epoch (not
          // `this.conn` identity) is what makes this hold across the windows
          // where `this.conn` is legitimately null — mid-replacement, and
          // after an idle close, which is exactly when a zombie socket's
          // frames used to slip through.
          const opened = await openWsChatConnection({
            url,
            traceId,
            onEvent: (event, sessionId) => {
              if (isStale()) return;
              this.route(event, sessionId);
            },
            onOpen: () => {
              if (isStale()) return;
              this.setStatus(ConnectionStatus.CONNECTED);
              this.reconnectAttempts = 0;
              this.awaitingPresence = false;
              this.startPing();
              // publish_op frames sent while we were away are gone — read them back.
              this.refreshWatchedPublishOps();
            },
            onClose: event => {
              console.info('[WS chat] socket closed:', {
                traceId,
                code: event.code,
                reason: event.reason,
                wasClean: event.wasClean,
                stale: isStale(),
              });
              if (isStale()) return;
              this.conn = null;
              this.stopPing();
              this.setStatus(ConnectionStatus.DISCONNECTED);
              // The connection is gone — any agentType priming on it is void;
              // a reconnect (or fresh connect) must re-prime from scratch.
              this.readyAgents.clear();
              // Our own close (user change, idle shutdown) — never reconnect.
              if (this.intentionalClose) {
                this.intentionalClose = false;
                return;
              }
              // Unexpected drop (network, upstream, LB idle timeout): reconnect.
              this.scheduleReconnect();
            },
            onError: () => {
              if (isStale()) return;
              this.setStatus(ConnectionStatus.DISCONNECTED);
            },
          });
          if (isStale()) {
            // Torn down (reset, user switch, idle close) while the handshake
            // was in flight: the handlers above are already stale, so
            // installing this socket would hand later sends an open
            // connection whose every reply is discarded. Close it and reject.
            opened.close();
            throw new StaleConnectError();
          }
          return opened;
        } catch (e) {
          // A failure on an attempt that was torn down mid-flight is not a
          // connect failure: reclassify it so the caller neither runs the
          // fresh-token retry as the stale captured userId nor tears down a
          // replacement connection that already took over.
          if (!(e instanceof StaleConnectError) && isStale()) {
            throw new StaleConnectError();
          }
          throw e;
        }
      };

      let conn: WsChatConnection;
      try {
        conn = await open(force);
      } catch (e) {
        // A stale abort is not a connect failure — retrying would install a
        // connection the teardown just forbade.
        if (force || e instanceof StaleConnectError) throw e;
        console.warn('[WS chat] initial connect failed; retrying with a fresh token:', e);
        clearWsChatSession();
        // Internal cleanup between attempts of ONE ensureConnection call:
        // keep `this.connecting` registered so a concurrent caller joins this
        // attempt's retry instead of forking a competing socket.
        this.closeConnection(false);
        // closeConnection marks its teardown intentional, but this run is
        // still deliberately connecting — and with stale onClose handlers now
        // returning early, nothing else would ever consume the flag. Without
        // this reset the retry connection's next unexpected drop would read
        // as intentional and never auto-reconnect.
        this.intentionalClose = false;
        conn = await open(true);
      }
      this.conn = conn;
    })();

    const tracked = run.catch(e => {
      if (!(e instanceof StaleConnectError)) {
        this.setStatus(ConnectionStatus.DISCONNECTED);
      }
      throw e;
    });
    // Identity-guarded clear: closeConnection() may have already detached this
    // attempt and a NEWER connect may own the slot by the time this settles.
    const handle: Promise<void> = tracked.finally(() => {
      if (this.connecting === handle) this.connecting = null;
    });
    this.connecting = handle;
    this.connectingIsForced = force;
    return handle;
  }

  /**
   * @param detachInFlightConnect leave true for an external teardown (reset,
   *        idle close, user switch) — it detaches any in-flight connect, which
   *        is doomed (the epoch bump makes it abort with StaleConnectError),
   *        so a later caller opens its own connection instead of inheriting
   *        that rejection. The fresh-token retry passes false: its cleanup is
   *        internal to ONE still-running attempt, which stays joinable.
   */
  private closeConnection(detachInFlightConnect = true): void {
    this.intentionalClose = true;
    this.clearReconnectTimer();
    this.stopPing();
    this.connEpoch++; // whatever socket existed is stale from here on
    if (detachInFlightConnect) {
      this.connecting = null;
    }
    this.conn?.close();
    this.conn = null;
    this.setStatus(ConnectionStatus.DISCONNECTED);
    this.readyAgents.clear();
  }

  /**
   * Route a server frame to its conversation's reducer. The backend always
   * tags multiplexed, per-conversation frames with `sessionId` (spec §3
   * inv.4(a)); a frame without one can't be attributed to a single
   * conversation and, worse, used to silently fall back to "whichever
   * session sent last" — which is exactly the cross-agent bleed this
   * multiplexing work fixes.
   *
   * chatbot-api's `DigitalStaffWsProxyHandler` also legitimately sends a few
   * connection-level, agent-agnostic frames with NO sessionId by design —
   * `CREDIT_EXHAUSTED` on a fresh connect, the "too many conversations"
   * session-limit rejection, and the per-frame credit gate all `send()`
   * directly on the socket rather than going through the per-session
   * `stampSessionId` fan-out. All three land here as `{type:'error', ...}`
   * (optionally with `code:'CREDIT_EXHAUSTED'`, which `frameToEvent` already
   * narrows to `credit-exhausted`). Since they're agent-agnostic there's no
   * misattribution risk in fanning them out to every live conversation, so we
   * broadcast instead of dropping. Anything else without a sessionId is a
   * genuine conversation-scoped (agent-reply) frame that arrived unrouted —
   * that's a real bug indicator, so it's dropped and logged rather than
   * guessed at.
   */
  private route(event: WsChatEvent, sessionId: string | null): void {
    if (!sessionId || !sessionId.trim()) {
      if (isGlobalFrame(event)) {
        console.warn('[WS chat] broadcasting global frame with no sessionId:', event);
        this.broadcastEvent(event);
        return;
      }
      console.warn('[WS chat] dropping agent-reply frame with no sessionId:', event);
      return;
    }
    // Publish progress is not part of any turn: it must not end a queued hold, count as the
    // turn's activity (recoverStuckTurns reads lastEventAt as "streaming resumed"), or reach
    // the turn reducer. It only updates the conversation's publish-op map.
    if (event.type === 'publish-op') {
      this.applyPublishOps(sessionId, [event.op]);
      return;
    }
    const slot = this.slot(sessionId);
    // Any frame for this conversation — the queued turn's `resume`, or its first frame — means
    // the wait the hold below covers is over.
    this.clearQueuedHold(slot);
    const patch: Partial<WsChatSessionEntry> = {
      state: wsChatReducer(slot.entry.state, event),
      lastEventAt: Date.now(),
    };
    // [3.5 #16] Any inbound frame is activity. This used to be stamped only in
    // `sendMessage`, so the 60s RECENT_ACTIVITY_WINDOW_MS expired in the middle
    // of long multi-turn work (batch generation) and `scheduleReconnect` then
    // gave up on a drop with "idle → lazy reconnect on next send".
    this.lastActivityAt = Date.now();
    // Re-attached to a still-streaming turn after a refresh/detach: keep the UI
    // in the waiting state so incoming live frames land on the active turn.
    if (event.type === 'resume') {
      patch.pendingReply = event.replyInFlight;
      // The proxy says the turn already ended while we were detached, but our UI
      // is still mid-stream (stuck spinner from a half-open socket). The reducer
      // treats resume{replyInFlight:false} as a no-op and recoverStuckTurns skips
      // it now that pendingReply is false — so re-hydrate from persisted history
      // here to restore the finished reply and unlock the composer.
      if (!event.replyInFlight && slot.entry.state.phase === 'streaming') {
        this.setEntry(slot, patch);
        this.clearInflight(sessionId);
        void this.resyncFromHistory(sessionId);
        return;
      }
    }
    // The turn is over (or errored) — no reply in-flight anymore.
    if (event.type === 'message-complete' || event.type === 'error') {
      patch.pendingReply = false;
      this.clearInflight(sessionId);
    }
    // A failed turn: chatbot-api still sends anything it queued behind it, and says so with a
    // `resume` frame, which puts the conversation back to work on its own.
    if (event.type === 'error') slot.queuedUnanswered = 0;
    this.setEntry(slot, patch);
    if (event.type === 'message-complete') {
      // This turn ended, but a message the merchant sent while it was running has not been
      // answered — chatbot-api is sending it up as the next turn. Stay "working" until that
      // turn shows up rather than flashing idle in between. Before the listeners run, so a
      // send they make (the publish executor's outcome) is counted the same way.
      if (slot.queuedUnanswered > 0) {
        slot.queuedUnanswered -= 1;
        this.holdForQueuedTurn(sessionId, slot);
      }
      this.turnCompleteListeners.forEach(l => l(sessionId));
      this.maybeIdleClose();
    }
  }

  /** See QUEUED_TURN_START_TIMEOUT_MS. */
  private holdForQueuedTurn(sessionId: string, slot: SessionSlot): void {
    this.setEntry(slot, {
      state: wsChatReducer(slot.entry.state, { type: 'resume', replyInFlight: true }),
      pendingReply: true,
    });
    this.markInflight(sessionId, slot.agentType);
    slot.queuedHoldTimer = setTimeout(() => {
      slot.queuedHoldTimer = null;
      // Nothing came: the queued turn is not coming (or already ran out of sight). Back to
      // idle without an error — there is nothing the merchant did wrong or needs to redo.
      slot.queuedUnanswered = 0;
      this.setEntry(slot, {
        state: wsChatReducer(slot.entry.state, { type: 'interrupt' }),
        pendingReply: false,
      });
      this.clearInflight(sessionId);
      this.maybeIdleClose();
    }, QUEUED_TURN_START_TIMEOUT_MS);
  }

  private clearQueuedHold(slot: SessionSlot): void {
    if (slot.queuedHoldTimer) {
      clearTimeout(slot.queuedHoldTimer);
      slot.queuedHoldTimer = null;
    }
  }

  /** Close the shared socket once nothing is mounted and nothing streams. */
  private maybeIdleClose(): void {
    if (this.refCount > 0) return;
    if (this.anySessionActive()) return;
    if (this.conn || this.connecting) this.closeConnection();
  }

  /**
   * [3.5 #16] "The server is still working on this conversation."
   *
   * `pendingReply` answers a narrower question — "is the reply to the message
   * *I* sent still outstanding" — and it is cleared on the FIRST
   * `message-complete`. Batch generation legitimately spans several assistant
   * messages (the drafts land on later ones, see WsChatMessageStream's
   * batch-progress notes), so from the second turn onwards `pendingReply` was
   * false while the run was very much alive. Everything that decides whether
   * to keep/restore the socket used that flag, so navigating away or hiding
   * the tab mid-batch closed the connection and the remaining frames were lost
   * for good — the spinner then span forever because `phase` can only be reset
   * by a frame.
   *
   * `phase` is the reducer's own view of the run, so it stays truthful across
   * turn boundaries. Both signals together are the right predicate.
   */
  private isSessionActive(slot: SessionSlot): boolean {
    if (slot.entry.pendingReply) return true;
    const { phase } = slot.entry.state;
    return phase === 'thinking' || phase === 'streaming';
  }

  private anySessionActive(): boolean {
    for (const slot of this.sessions.values()) {
      if (this.isSessionActive(slot)) return true;
    }
    return false;
  }

  // Auto-reconnect on an unexpected drop — but only when it's worth it:
  //  1) the user is present (tab visible + online),
  //  2) a conversation is still being worked on OR the user was recently active.
  // Otherwise we stay idle and reconnect lazily on the next send. While the
  // user is away we defer and resume the moment they return (presence handler).
  private scheduleReconnect(): void {
    if (this.reconnectTimer || this.conn?.isOpen()) return;

    const recentlyActive =
      this.anySessionActive() || Date.now() - this.lastActivityAt < RECENT_ACTIVITY_WINDOW_MS;
    if (!recentlyActive) return; // idle → lazy reconnect on next send

    if (!userPresent()) {
      this.awaitingPresence = true; // resume when the user comes back
      return;
    }
    if (this.reconnectAttempts >= RECONNECT_DELAYS_MS.length) {
      this.broadcastError(
        'Connection lost. Send a message to reconnect.',
        CLIENT_ERROR_CODES.CONNECTION_LOST
      );
      return;
    }

    const delay = RECONNECT_DELAYS_MS[this.reconnectAttempts];
    this.reconnectAttempts += 1;
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.conn?.isOpen()) return;
      const stillActive =
        this.anySessionActive() || Date.now() - this.lastActivityAt < RECENT_ACTIVITY_WINDOW_MS;
      if (!stillActive) return;
      if (!userPresent()) {
        this.awaitingPresence = true;
        return;
      }
      this.ensureConnection(this.connectionAgentType, true)
        .then(() => this.recoverStuckTurns())
        .catch(() => this.scheduleReconnect());
    }, delay);
  }

  /**
   * After a reconnect, the backend re-attaches live conversations and resumes
   * streaming on its own (the final `done` frame carries the full reply, so a
   * mid-stream gap self-heals). Conversations whose turn got no frames shortly
   * after reconnecting were finalized server-side — resync those from history.
   */
  private recoverStuckTurns(): void {
    const reconnectedAt = Date.now();
    setTimeout(() => {
      this.sessions.forEach((slot, sessionId) => {
        if (!this.isSessionActive(slot)) return;
        if (slot.entry.lastEventAt >= reconnectedAt) return; // streaming resumed
        this.setEntry(slot, { pendingReply: false });
        this.clearInflight(sessionId); // upstream turn is gone — don't retry next mount
        void this.resyncFromHistory(sessionId);
      });
    }, POST_RECONNECT_RESYNC_DELAY_MS);
  }

  /**
   * Resync a conversation to chatbot-api's recorded history (the source of
   * truth) so a half-streamed reply is replaced with what was actually
   * persisted and the stuck turn is unblocked.
   *
   * The conversation's agentType (from its slot) is required so history is
   * fetched from the right agent's log — `getConversationDetail(userId,
   * sessionId, '')` would otherwise be ambiguous the same way an unrouted
   * frame is. If the slot has no agentType yet (shouldn't happen for a
   * conversation that ever had a pending reply, since that only comes from
   * `sendMessage`, which always stamps one — but defend anyway), log and skip
   * rather than guessing.
   */
  async resyncFromHistory(sessionId: string): Promise<void> {
    const userId = this.userId;
    if (!userId) return;
    const agentType = this.getSessionAgentType(sessionId);
    if (!agentType) {
      console.warn('[WS chat] resync skipped: no agentType on slot for session', sessionId);
      return;
    }
    try {
      const { messages, traces } = await getConversationDetail(userId, sessionId, agentType);
      if (messages.length > 0) {
        this.hydrate(sessionId, messages, traces, agentType);
      } else {
        this.dispatch(sessionId, { type: 'interrupt' });
      }
    } catch (e) {
      console.warn('[WS chat] resync after reconnect failed:', e);
      this.dispatch(sessionId, { type: 'interrupt' });
    }
  }

  private broadcastError(message: string, code?: string): void {
    this.sessions.forEach(slot => {
      if (slot.entry.pendingReply) {
        this.setEntry(slot, {
          state: wsChatReducer(slot.entry.state, { type: 'error', message, code }),
          pendingReply: false,
        });
      }
    });
  }

  /**
   * Broadcast a connection-level frame (arrived with no sessionId, see
   * `route`) to EVERY live conversation, not just ones with a pending reply —
   * unlike `broadcastError` above, credit-exhausted/session-limit frames can
   * arrive with no turn in flight at all (e.g. straight after a fresh
   * connect), and every open conversation still needs to surface them.
   */
  private broadcastEvent(event: WsChatEvent): void {
    this.sessions.forEach(slot => {
      this.setEntry(slot, {
        state: wsChatReducer(slot.entry.state, event),
        pendingReply: false,
      });
    });
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.awaitingPresence = false;
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      const conn = this.conn;
      if (!conn?.isOpen()) return;
      conn.sendRaw({ type: 'ping' });
      // Half-open detection: a reply is streaming but the receive side has been
      // silent past the stall timeout → the socket is dead-but-open. Force a
      // reconnect (onClose won't fire on a half-open socket, so nothing else
      // will). The reconnect re-attaches / resyncs the stuck turn from history.
      if (this.anySessionActive() && Date.now() - conn.lastFrameAt() > STALL_TIMEOUT_MS) {
        console.warn(
          '[WS chat] half-open socket detected (no frames while streaming) — reconnecting'
        );
        this.forceReconnect();
      }
    }, PING_INTERVAL_MS);
  }

  /**
   * Tear down a stalled/half-open socket and reconnect. Mirrors the socket's own
   * onClose teardown, but a half-open socket never fires onClose — so we do it by
   * hand. `conn.close()` nulls the socket handlers, so no onClose fires here
   * (no double reconnect); we drive scheduleReconnect ourselves.
   */
  private forceReconnect(): void {
    this.stopPing();
    this.connEpoch++; // the half-open socket is stale from here on
    this.conn?.close();
    this.conn = null;
    this.readyAgents.clear();
    this.setStatus(ConnectionStatus.DISCONNECTED);
    this.scheduleReconnect();
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /** Resume a deferred reconnect the moment the user returns to the tab/network. */
  private bindPresence(): void {
    if (this.presenceBound || typeof document === 'undefined') return;
    this.presenceBound = true;
    const onPresence = () => {
      if (!userPresent()) {
        // Hidden/offline: only drop the socket when nothing is being worked on.
        if (!this.anySessionActive()) this.closeConnection();
        return;
      }
      // [3.5 #16] Back on the tab (or back online) — reconcile directly instead
      // of going through `scheduleReconnect`, and never gate this on
      // `awaitingPresence`. That flag is cleared by `clearReconnectTimer()`,
      // which `closeConnection()` calls, so after the branch above closed an
      // idle socket it was always false and this whole path was dead code: the
      // socket stayed shut until the user happened to send another message.
      this.awaitingPresence = false;
      if (this.conn?.isOpen() || this.connecting || this.reconnectTimer) return;
      const worthReconnecting =
        this.anySessionActive() || Date.now() - this.lastActivityAt < RECENT_ACTIVITY_WINDOW_MS;
      if (!worthReconnecting) return; // truly idle → lazy reconnect on next send
      this.reconnectAttempts = 0;
      this.ensureConnection(this.connectionAgentType, true)
        .then(() => this.recoverStuckTurns())
        .catch(e => console.warn('[WS chat] presence reconnect failed:', e));
    };
    document.addEventListener('visibilitychange', onPresence);
    window.addEventListener('online', onPresence);
  }

  /**
   * [3.5 #16] Stuck-turn watchdog — the last line of defence.
   *
   * `phase` is only ever reset by a frame (`message-complete` / `error` /
   * `interrupt`), so any frame we never receive used to strand the UI forever:
   * spinner spinning, composer disabled, batch summary card never rendered.
   * Once a conversation that reads as active has been silent past
   * STUCK_TURN_TIMEOUT_MS we first try to get the socket back (the run may
   * still be alive upstream, in which case `recoverStuckTurns` sorts it out),
   * and otherwise reconcile against persisted history — which either restores
   * the finished reply or interrupts, both of which unlock the UI.
   */
  private startWatchdog(): void {
    if (this.watchdogTimer || typeof setInterval === 'undefined') return;
    this.watchdogTimer = setInterval(() => this.checkStuckTurns(), WATCHDOG_INTERVAL_MS);
  }

  private stopWatchdog(): void {
    if (this.watchdogTimer) {
      clearInterval(this.watchdogTimer);
      this.watchdogTimer = null;
    }
  }

  private checkStuckTurns(): void {
    if (!userPresent()) return; // don't fight the presence handler while hidden
    const now = Date.now();
    this.sessions.forEach((slot, sessionId) => {
      if (!this.isSessionActive(slot)) return;
      // A slot that has never seen a frame falls back to the last send, so a
      // turn that dies before its first frame is covered too.
      //
      // The conversation's own last send counts as a signal too. `lastEventAt` is the last
      // frame of the PREVIOUS turn, so a message sent after a long pause (reading a draft for
      // a quarter of an hour) read as "silent for 15 minutes" the moment it went out, and the
      // next tick resynced it from history: "Maya is thinking…" vanished seconds after the
      // send, before the model's first frame (8-15s on a long context, measured 10-26) —
      // the same "nothing is happening" that gets a message sent a second time.
      const lastSignalAt = Math.max(slot.entry.lastEventAt, slot.lastSentAt) || this.lastActivityAt;
      if (!lastSignalAt || now - lastSignalAt < STUCK_TURN_TIMEOUT_MS) return;
      const socketOpen = !!this.conn?.isOpen();
      if (socketOpen && now - lastSignalAt < STUCK_TURN_LIVE_SOCKET_TIMEOUT_MS) return;

      if (!socketOpen && !this.connecting && !this.reconnectTimer) {
        console.warn('[WS chat] stuck turn with no socket — reconnecting', sessionId);
        this.reconnectAttempts = 0;
        this.ensureConnection(this.connectionAgentType, true)
          .then(() => this.recoverStuckTurns())
          .catch(() => {
            this.setEntry(slot, { pendingReply: false });
            this.clearInflight(sessionId);
            void this.resyncFromHistory(sessionId);
          });
        return;
      }
      if (socketOpen) {
        // Socket is up yet this conversation has gone quiet for longer than the
        // server itself tolerates — the upstream turn ended without us seeing
        // it. Take history as the truth.
        console.warn('[WS chat] stuck turn on a live socket — resyncing', sessionId);
        this.setEntry(slot, { pendingReply: false });
        this.clearInflight(sessionId);
        void this.resyncFromHistory(sessionId);
      }
    });
  }
}

/** The app-wide singleton: one socket, many conversations. */
export const wsChatManager = new WsChatManager();
